import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "modificationsField", "priceVariantField", "variantNameField", "customSizeField", "percentageIncreaseField",
        "product", "quantity", "computedPrice", "subtotal", "addonPriceField", "bomPriceField", "priceField", "subtotalField",
        "isBomTypeCheckbox",
        "bomButton"
    ]

    connect() {
        this.toggleBomButton()

        this.spinnerEl = document.getElementById("frame-spinner")
        this.frameEl   = document.getElementById("mod-widget-frame")

        this._onBeforeFetch = this._onBeforeFetch.bind(this)
        this._onFrameLoad   = this._onFrameLoad.bind(this)
        this._onFetchError  = this._onFetchError.bind(this)

        document.addEventListener("turbo:before-fetch-request", this._onBeforeFetch)
        document.addEventListener("turbo:frame-load", this._onFrameLoad)
        document.addEventListener("turbo:fetch-request-error", this._onFetchError)
    }

    disconnect() {
        document.removeEventListener("turbo:before-fetch-request", this._onBeforeFetch)
        document.removeEventListener("turbo:frame-load", this._onFrameLoad)
        document.removeEventListener("turbo:fetch-request-error", this._onFetchError)
    }

    // Spinner helpers
    _showSpinner() {
        this.spinnerEl?.classList.remove("hidden")
    }
    _hideSpinner() {
        this.spinnerEl?.classList.add("hidden")
    }

    _onBeforeFetch(e) {
        if (e.target === this.frameEl) this._showSpinner()
    }
    _onFrameLoad(e) {
        if (e.target === this.frameEl) this._hideSpinner()
    }
    _onFetchError(e) {
        if (e.target === this.frameEl) this._hideSpinner()
    }

    toggleBomButton(event) {
        const field = event?.target || this.productTarget
        let isBom = false
        if (field.tagName === "SELECT") {
            isBom = field.options[field.selectedIndex]?.dataset.isBom === "true"
        } else {
            isBom = field.dataset.isBom === "true"
        }
        this.bomButtonTarget.classList.toggle("hidden", !isBom)
        this.bomButtonTarget.classList.toggle("inline-flex", isBom)
    }

    openBomModal() {
        const modalEl = document.getElementById("bom-modal");
        if (!modalEl) return;

        // remember which quotation line row opened the modal
        modalEl.dataset.rowId = this.element.id;

        // ask bom-modal controller to populate UI from this row’s hidden inputs
        const bomModal =
            this.application.getControllerForElementAndIdentifier(modalEl, "bom-modal");

        if (bomModal?.openFromRow) {
            bomModal.openFromRow(this.element);
            return;
        }

        // fallback: show empty modal if controller method not present yet
        const content = modalEl.querySelector("[data-bom-modal-target='content']");
        if (content) content.innerHTML = "";
        modalEl.classList.remove("hidden");
        modalEl.classList.add("flex");
    }

    applyBom(subtotal) {
        this.bomPriceFieldTarget.value = subtotal
        this.update_price?.()
    }

    applyModifications({ variantPrice, variantName, mods, addonPrice, percentageIncrease, customSize }) {
        if (this.hasPriceVariantFieldTarget) this.priceVariantFieldTarget.value = variantPrice || ""
        if (this.hasVariantNameFieldTarget) this.variantNameFieldTarget.value = variantName || ""
        if (this.hasModificationsFieldTarget) this.modificationsFieldTarget.value = JSON.stringify(mods || [])
        if (this.hasCustomSizeFieldTarget) this.customSizeFieldTarget.value = (customSize || "").trim()
        if (this.hasPercentageIncreaseFieldTarget) this.percentageIncreaseFieldTarget.value = percentageIncrease || 0.0
        if (this.hasAddonPriceFieldTarget) this.addonPriceFieldTarget.value = addonPrice || 0.0
        this.update_price?.()
    }

    update_price() {
        const productId = this.productTarget?.value
        const quantity = this.quantityTarget?.value
        const addon_price = this.addonPriceFieldTarget?.value
        const bom_price = this.bomPriceFieldTarget?.value
        if (!productId || !quantity) return

        let selectedPriceVariant = ""
        let selectedModifications = []

        if (this.hasPriceVariantFieldTarget && this.priceVariantFieldTarget.value) {
            selectedPriceVariant = this.priceVariantFieldTarget.value
        }

        if (this.hasModificationsFieldTarget && this.modificationsFieldTarget.value) {
            try {
                selectedModifications = JSON.parse(this.modificationsFieldTarget.value)
            } catch(e) {
                console.warn("Invalid JSON in modificationsField:", e)
            }
        }

        const normalizedMods = (selectedModifications || []).map(m => {
            if (typeof m === "string") return { value: m, qty: 1 }
            const qty = Math.max(1, parseInt(m.qty, 10) || 1)
            return { value: m.value, qty }
        })

        const params = new URLSearchParams({
            product_id: productId,
            quantity,
            price_variant: selectedPriceVariant,
            modifications_json: JSON.stringify(normalizedMods),
            addon_price,
            bom_price
        })

        fetch(`/quotation_lines/compute?${params.toString()}`)
            .then(r => r.json())
            .then(data => {
                const price = parseFloat(data.computed_price || 0)
                const subtotal = parseFloat(data.subtotal || 0)

                this.computedPriceTarget.innerText = `₱${price.toFixed(2)}`
                this.subtotalTarget.innerText = `₱${subtotal.toFixed(2)}`
                this.priceFieldTarget.value = price
                this.subtotalFieldTarget.value = subtotal

                const groupId = this.element.dataset.parentDom
                if (groupId) {
                    this.element.dispatchEvent(new CustomEvent("quotation:line-total-updated", {
                        bubbles: true,
                        detail: { groupId }
                    }))
                }

                const quotationElement = this.element.closest("[data-controller~='quotation']")
                if (quotationElement) {
                    const controller = this.application.getControllerForElementAndIdentifier(quotationElement, "quotation")
                    controller?.computeTotal()
                }
            })
    }

    openWidget(event) {
        const lineIndex  = event.target.dataset.lineIndex
        const productId  = this.productTarget?.value
        if (!productId) {
            alert("Please select a product first.")
            return
        }
        const modal      = document.getElementById("modifications-modal")
        const frame      = this.frameEl || document.getElementById("mod-widget-frame")

        // Show modal
        modal.dataset.rowId = this.element.id
        modal.classList.remove("hidden")
        modal.classList.add("flex")

        // Build URL
        const priceVariant  = this.priceVariantFieldTarget?.value || ""
        const variantName   = this.variantNameFieldTarget?.value || ""
        const modifications = this.modificationsFieldTarget?.value || "[]"
        const percentageIncrease = this.percentageIncreaseFieldTarget.value || ""
        const customSize = this.customSizeFieldTarget.value || ""
        const addon_price = this.addonPriceFieldTarget.value || ""
        const url = `/products/${productId}/modifications_form` +
            `?index=${encodeURIComponent(lineIndex)}` +
            `&variant=${encodeURIComponent(priceVariant)}` +
            `&variantName=${encodeURIComponent(variantName)}` +
            `&percentageIncrease=${encodeURIComponent(percentageIncrease)}` +
            `&addonPrice=${encodeURIComponent(addon_price)}` +
            `&customSize=${encodeURIComponent(customSize)}` +
            `&mods=${encodeURIComponent(modifications)}`

        // Spinner on
        this._showSpinner()

        // One-time success handler
        const onLoad = () => {
            this._hideSpinner()

            // ---- Custom Size handling (inside the loaded frame) ----
            // Checkbox
            const customSizeCheckbox =
                frame.querySelector('input[name="is_custom_size"]') ||
                frame.querySelector('[data-modal-target="customSizeCheckbox"]')

            // Section to reveal/hide
            const customSizeSection =
                frame.querySelector('[data-modal-target="customSizeSection"]') ||
                frame.querySelector('[data-reveal-target="item"]')

            // Input
            const customSizeInput =
                frame.querySelector('#modal_custom_size') ||
                frame.querySelector('[data-modal-target="customSizeInput"]')

            const hasCustom = !!customSize && customSize.trim().length > 0

            if (customSizeCheckbox) customSizeCheckbox.checked = hasCustom
            if (customSizeSection)  customSizeSection.classList.toggle('hidden', !hasCustom)
            if (customSizeInput && hasCustom) {
                customSizeInput.value = customSize
            }

            const modalCtrl = this.application.getControllerForElementAndIdentifier(modal, "modal")
            if (modalCtrl && this.hasPercentageIncreaseFieldTarget) {
                const percentage = parseFloat(this.percentageIncreaseFieldTarget.value || "0")
                if (!Number.isNaN(percentage) && percentage > 0) {
                    modalCtrl.applyCustomSize({ detail: { percentage } })
                }
            }
        }

        frame.addEventListener("turbo:frame-load", onLoad,   { once: true })

        // Trigger load after listeners are attached
        frame.src = url
    }

    remove(event) {
        event.preventDefault()

        if (!confirm("Are you sure you want to remove this line?")) {
            return
        }

        const quotationEl = this.element.closest("[data-controller~='quotation']")
        const quotationCtrl = quotationEl
            ? this.application.getControllerForElementAndIdentifier(quotationEl, "quotation")
            : null

        const container =
            this.element.closest('[data-controller~="quotation-lines"]') || document

        const isGroupRow = this.element.hasAttribute("data-group-row")

        if (isGroupRow) {
            // ---- GROUP ROW LOGIC ----
            const groupId = this.element.dataset.groupId
            if (!groupId) return

            // 1) mark the group header row
            this._markRowForDestroy(this.element)

            // 2) mark all members under this group
            const childRows = container.querySelectorAll(
                `tr[data-parent-dom="${groupId}"]`
            )

            childRows.forEach((row) => {
                this._markRowForDestroy(row, { skipRecompute: true })
            })

            // 3) recompute once for this group
            container.dispatchEvent(
                new CustomEvent("quotation:line-total-updated", {
                    bubbles: true,
                    detail: { groupId, reason: "group-remove" },
                })
            )
        } else {
            // ---- SINGLE LINE LOGIC (your existing behavior) ----
            const groupId = this.element.dataset.parentDom

            this._markRowForDestroy(this.element)

            if (groupId) {
                container.dispatchEvent(
                    new CustomEvent("quotation:line-total-updated", {
                        bubbles: true,
                        detail: { groupId, reason: "remove" },
                    })
                )
            }
        }

        // Optional: update overall totals
        quotationCtrl?.computeTotal()
    }

// Helper to DRY the row removal logic
    _markRowForDestroy(row, { skipRecompute = false } = {}) {
        const persistedIdInput = row.querySelector('input[name$="[id]"]')
        const destroyInput = row.querySelector(
            'input[name$="[_destroy]"], input[data-destroy-field]'
        )

        if (persistedIdInput && destroyInput) {
            // Saved row: mark as destroyed + hide
            destroyInput.value = "1"
            row.classList.add("hidden")
            row.setAttribute("aria-hidden", "true")
        } else {
            // Unsaved row: hide, then remove
            row.classList.add("hidden")
            row.setAttribute("aria-hidden", "true")
            requestAnimationFrame(() => row.remove())
        }
    }

}
